import type { ResourceServiceType } from "@core-ui/ContextServices/ResourceService/ResourceService";
import withObjectUrl from "@core-ui/utils/withObjectUrl";
import { canAddToGallery } from "@ext/markdown/elements/gallery/edit/logic/addToGallery";
import createGallery, { addImagesToGallery } from "@ext/markdown/elements/gallery/edit/logic/createGallery";
import getNaturalSize from "@ext/markdown/elements/image/edit/logic/getNaturalSize";
import { storeImageFile } from "@ext/markdown/elements/image/edit/logic/saveImageFile";
import { MAX_INLINE_IMAGE_HEIGHT } from "@ext/markdown/elements/inlineImage/edit/models/node";
import type { Attrs, ResolvedPos } from "@tiptap/pm/model";
import { NodeSelection, Selection, type Transaction } from "@tiptap/pm/state";
import type { EditorView } from "prosemirror-view";

const createBlockImage = (view: EditorView, From: ResolvedPos, attributes: Attrs): Transaction => {
	const tr = view.state.tr;

	const node = view.state.schema.nodes.image.create(attributes);
	tr.replaceSelectionWith(node);
	if (From.parentOffset === 0 && From.parent.isTextblock)
		tr.setSelection(Selection.near(tr.doc.resolve(From.pos - 1)));
	else tr.setSelection(Selection.near(tr.doc.resolve(From.pos + 1)));

	return tr;
};

const createInlineImage = (view: EditorView, attributes: Attrs): Transaction => {
	const tr = view.state.tr;

	const node = view.state.schema.nodes.inlineImage.create(attributes);
	tr.replaceSelectionWith(node);

	return tr;
};

const createImages = async (
	files: File[],
	view: EditorView,
	fileName: string,
	resourceService: ResourceServiceType,
	caption?: string,
) => {
	const filteredFiles = files.filter((f) => f);
	if (!filteredFiles.length) return;

	const images = filteredFiles.filter((file) => file.type.startsWith("image"));
	if (images.length > 1 && view.state.schema.nodes.gallery) {
		const { selection, doc } = view.state;
		if (selection instanceof NodeSelection && canAddToGallery(doc, selection.from))
			return addImagesToGallery(images, view, selection.node, fileName, resourceService);
		return createGallery(images, view, fileName, resourceService);
	}

	for (const file of filteredFiles) {
		if (!file.type.startsWith("image")) continue;

		const { $from, $to } = view.state.selection;
		let attributes: Attrs = { src: "" };

		const newSize = await withObjectUrl(file, getNaturalSize);

		let isInline = false;

		if (!caption && $from.parent.isTextblock && $from.parent.childCount) isInline = true;
		if (newSize) {
			attributes = {
				...attributes,
				width: `${newSize.width}px`,
				height: `${newSize.height}px`,
			};

			if (!isInline && !caption) isInline = newSize.height <= MAX_INLINE_IMAGE_HEIGHT;
		}

		if (isInline && ($from.parent === view.state.doc.firstChild || $to.parent === view.state.doc.firstChild))
			continue;

		attributes = { ...attributes, src: await storeImageFile(file, fileName, resourceService) };

		const tr = isInline
			? createInlineImage(view, attributes)
			: createBlockImage(view, $from, { ...attributes, title: caption });
		view.dispatch(tr);
	}
};

export default createImages;
